import React, { useEffect, useState } from "react";
import { Aviso, carregarPessoas, chamarSlack, EstadoSlack, MENSAGEM_ERRO_SLACK, PessoaSlack, responsavelDo } from "./slackPainel";

// "Avisar no Slack" de uma pergunta do painel: escolhe quem marcar (já vem o responsável do app),
// um recado opcional, e manda no canal da central (api/slack.ts). Depois mostra o selo "avisado".
interface Props {
  id: number | string; // número da resposta (perguntas.id)
  slugs: string[]; // slugs do app (português e espanhol)
  appNome: string;
  aviso: Aviso | null;
  slack: EstadoSlack | null;
  compacto?: boolean; // nas linhas de "Lacunas": só o botão, sem texto extra
}

const AvisarSlack: React.FC<Props> = ({ id, slugs, appNome, aviso, slack, compacto = false }) => {
  const [aberto, setAberto] = useState(false);
  const [pessoas, setPessoas] = useState<PessoaSlack[] | null>(null);
  const [destino, setDestino] = useState("");
  const [recado, setRecado] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ultimo, setUltimo] = useState<Aviso | null>(aviso);

  useEffect(() => setUltimo(aviso), [aviso]);

  const responsavel = responsavelDo(slack, slugs);

  useEffect(() => {
    if (!aberto) return;
    setDestino(responsavel?.slack_id || "");
    carregarPessoas()
      .then(setPessoas)
      .catch(() => setErro("Não consegui carregar as pessoas do Slack."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  if (!slack?.configurado) return null;

  const enviar = async () => {
    setEnviando(true);
    setErro(null);
    try {
      const r = await chamarSlack({ acao: "avisar", id, slackId: destino || "nenhum", recado: recado.trim() || undefined, appNome });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErro(MENSAGEM_ERRO_SLACK[json?.erro] || "Não consegui enviar. Tente de novo.");
        return;
      }
      setUltimo({ por: "você", quando: "agora" });
      setAberto(false);
      setRecado("");
    } catch {
      setErro("Sem conexão com o servidor.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className={`avisar-slack${compacto ? " compacto" : ""}`} onClick={(e) => e.stopPropagation()}>
      {!aberto && (
        <div className="avisar-slack-linha">
          {ultimo && (
            <span className="metricas-selo slack" title={`Avisado no Slack por ${ultimo.por} (${ultimo.quando})`}>
              avisado por {ultimo.por.split(" ")[0]} · {ultimo.quando}
            </span>
          )}
          <button type="button" className="avisar-slack-botao" onClick={() => setAberto(true)}>
            {ultimo ? "Avisar de novo" : "Avisar no Slack"}
          </button>
        </div>
      )}
      {aberto && (
        <div className="avisar-slack-caixa">
          <label>
            Marcar
            <select value={destino} onChange={(e) => setDestino(e.target.value)} disabled={!pessoas}>
              {!pessoas && <option value={destino}>{responsavel?.nome || "Carregando…"}</option>}
              {pessoas && <option value="">Ninguém (só enviar no canal)</option>}
              {pessoas?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                  {p.id === responsavel?.slack_id ? " (responsável)" : ""}
                </option>
              ))}
            </select>
          </label>
          <textarea
            placeholder="Recado (opcional), ex.: esse artigo precisa de um passo a passo"
            maxLength={500}
            rows={2}
            value={recado}
            onChange={(e) => setRecado(e.target.value)}
          />
          <div className="avisar-slack-acoes">
            <button type="button" className="avisar-slack-enviar" onClick={enviar} disabled={enviando}>
              {enviando ? "Enviando…" : "Enviar no Slack"}
            </button>
            <button type="button" className="usuarios-fechar" onClick={() => setAberto(false)} disabled={enviando}>
              Cancelar
            </button>
          </div>
          {erro && <p className="metricas-erro">{erro}</p>}
        </div>
      )}
    </div>
  );
};

export default AvisarSlack;
